import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import PaladinLogo from "../../svg/paladin-logo.svg";
import adminFetch from "../../utils/adminFetch";
import { NotificationProvider, useNotifications } from "../../context/NotificationContext";
import { useSocket } from "../../hooks/useSocket";
import { getTokenPayload } from "../../components/ProtectedRoute";

const INACTIVE_MS = 5 * 60 * 1000;
const COUNTDOWN_S = 59;

const BADGE_KEY = {
  "/admin/quotes":           "quotePending",
  "/admin/consultations":    "consultationPending",
  "/admin/contacts":         "contactUnread",
  "/admin/service-requests": "serviceRequestPending",
};

const NAV_SECTIONS = [
  {
    section: "Menu",
    items: [
      {
        to: "/admin", end: true, label: "Dashboard",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <rect x="3" y="3" width="8" height="8" rx="1.5"/>
            <rect x="13" y="3" width="8" height="8" rx="1.5"/>
            <rect x="13" y="13" width="8" height="8" rx="1.5"/>
            <rect x="3" y="13" width="8" height="8" rx="1.5"/>
          </svg>
        ),
      },
      {
        to: "/admin/quotes", label: "Quote Requests",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
        ),
      },
      {
        to: "/admin/consultations", label: "Consultations",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        ),
      },
      {
        to: "/admin/contacts", label: "Messages",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
          </svg>
        ),
      },
      {
      to: "/admin/referrals", label: "Referrals",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
    ],
  },
  {
    section: "General",
    items: [
      {
        to: "/admin/analytics", label: "Analytics",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
          </svg>
        ),
      },
      {
        to: "/admin/schedule", label: "Schedule",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="8" y1="18" x2="8" y2="18"/>
          </svg>
        ),
      },
      {
        to: "/admin/client-view", label: "Client View",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
            <line x1="19" y1="8" x2="23" y2="12"/>
            <line x1="23" y1="8" x2="19" y2="12"/>
          </svg>
        ),
      },
      {
        to: "/admin/service-requests", label: "Service Requests",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
          </svg>
        ),
      },
      {
        to: "/admin/voice-agent", label: "Voice Agent",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/>
          </svg>
        ),
      },
      {
        to: "/admin/testimonials", label: "Testimonials",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        ),
      },
      {
        to: "/admin/blog", label: "Blog",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
        ),
      },
      {
        to: "/admin/newsletter", label: "Newsletter",
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
        ),
      },
      {
        to: "/admin/login-logs", label: "Login Logs", superOnly: true,
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        ),
      },
      {
        to: "/admin/settings", label: "Settings", superOnly: true,
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        ),
      },
    ],
  },
];

// ── Search Dropdown ───────────────────────────────────────────────────
const STATUS_COLORS = {
  pending:     { bg: "#FEF3C7", text: "#92400E" },
  reviewed:    { bg: "#DBEAFE", text: "#1D4ED8" },
  quoted:      { bg: "#D1FAE5", text: "#065F46" },
  closed:      { bg: "#F3F4F6", text: "#6B7280" },
  contacted:   { bg: "#DBEAFE", text: "#1D4ED8" },
  completed:   { bg: "#D1FAE5", text: "#065F46" },
  unread:      { bg: "#FEF3C7", text: "#92400E" },
  read:        { bg: "#F3F4F6", text: "#6B7280" },
  replied:     { bg: "#D1FAE5", text: "#065F46" },
  replied:       { bg: "#D1FAE5", text: "#065F46" },
  "in-progress": { bg: "#DBEAFE", text: "#1D4ED8" },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#6B7280" };
  return (
    <span style={{ background: c.bg, color: c.text, padding: "2px 8px", borderRadius: "20px", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, fontFamily: "Arial, sans-serif" }}>
      {status}
    </span>
  );
}

function SearchSection({ title, items, onNavigate, path, renderItem }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: "1px solid #f1f5f9" }}>
        <span className="font-cinzel uppercase" style={{ fontSize: "9px", letterSpacing: "0.22em", color: "#94a3b8" }}>{title}</span>
        <button onClick={() => onNavigate(path)} className="font-cinzel uppercase transition-colors duration-150 hover:text-[#002DB5]" style={{ fontSize: "9px", letterSpacing: "0.15em", color: "#b0b8cc" }}>
          View all →
        </button>
      </div>
      {items.map(item => (
        <button key={item._id} onClick={() => onNavigate(path)} className="w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors duration-100" style={{ borderBottom: "1px solid #f8fafc" }}
          onMouseEnter={e => { e.currentTarget.style.background = "#f7f9ff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
          {renderItem(item)}
        </button>
      ))}
    </div>
  );
}

function SearchDropdown({ query, results, loading, onNavigate }) {
  const total = results ? Object.values(results).reduce((s, a) => s + a.length, 0) : 0;

  return (
    <div className="overflow-hidden" style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", boxShadow: "0 16px 40px rgba(1,46,114,0.12), 0 4px 12px rgba(0,0,0,0.06)", maxHeight: "460px", overflowY: "auto" }}>
      {loading && (
        <div className="flex items-center justify-center py-8 gap-3">
          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#002DB5" strokeWidth="2.5"><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
          <span className="font-cinzel uppercase" style={{ fontSize: "10px", letterSpacing: "0.18em", color: "#94a3b8" }}>Searching…</span>
        </div>
      )}

 {!loading && results && total > 0 && (
        <>
          <SearchSection title="Quote Requests" items={results.quotes} onNavigate={onNavigate} path="/admin/quotes"
            renderItem={q => (<><div className="min-w-0"><p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{q.firstName} {q.lastName}</p><p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{q.email}</p></div><StatusBadge status={q.status} /></>)}
          />
          <SearchSection title="Consultations" items={results.consultations} onNavigate={onNavigate} path="/admin/consultations"
            renderItem={c => (<><div className="min-w-0"><p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{c.fullName}</p><p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{c.email}</p></div><StatusBadge status={c.status} /></>)}
          />
          <SearchSection title="Messages" items={results.contacts} onNavigate={onNavigate} path="/admin/contacts"
            renderItem={c => (<><div className="min-w-0"><p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{c.name}</p><p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{c.subject}</p></div><StatusBadge status={c.status} /></>)}
          />
          <SearchSection title="Service Requests" items={results.serviceRequests} onNavigate={onNavigate} path="/admin/service-requests"
            renderItem={s => (
              <>
                <div className="min-w-0">
                  <p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{s.fullName}</p>
                  <p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{s.requestType} · {s.email}</p>
                </div>
                <StatusBadge status={s.status} />
              </>
            )}
          />
          <SearchSection title="Consultations" items={results.consultations} onNavigate={onNavigate} path="/admin/consultations"
            renderItem={c => (
              <>
                <div className="min-w-0">
                  <p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{c.fullName}</p>
                  <p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{c.email}</p>
                </div>
                <StatusBadge status={c.status} />
              </>
            )}
          />
          <SearchSection title="Messages" items={results.contacts} onNavigate={onNavigate} path="/admin/contacts"
            renderItem={c => (
              <>
                <div className="min-w-0">
                  <p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{c.name}</p>
                  <p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{c.subject}</p>
                </div>
                <StatusBadge status={c.status} />
              </>
            )}
          />
          <SearchSection title="Service Requests" items={results.serviceRequests} onNavigate={onNavigate} path="/admin/service-requests"
            renderItem={s => (
              <>
                <div className="min-w-0">
                  <p className="font-cinzel text-[#012E72] truncate" style={{ fontSize: "12px", letterSpacing: "0.06em", fontWeight: 600 }}>{s.fullName}</p>
                  <p className="font-constantia truncate" style={{ fontSize: "11px", color: "#94a3b8" }}>{s.requestType} · {s.email}</p>
                </div>
                <StatusBadge status={s.status} />
              </>
            )}
          />
        </>
      )}
    </div>
  );
}

// ── Inactivity Warning ────────────────────────────────────────────────
function InactivityWarning({ countdown, onStayLoggedIn, onLogoutNow }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const tRef      = useRef(0);
  const lastRef   = useRef(null);
  const urgent    = countdown <= 10;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cx = 60, cy = 60;
    const orbits = [
      { r: 44, dotR: 5,   speed: 0.9,  phase: 0,           alpha: 0.9  },
      { r: 33, dotR: 3.5, speed: -1.4, phase: Math.PI/3,   alpha: 0.65 },
      { r: 22, dotR: 2.5, speed: 2.1,  phase: Math.PI*0.8, alpha: 0.4  },
    ];
    function draw(ts) {
      if (!lastRef.current) lastRef.current = ts;
      const dt = (ts - lastRef.current) / 1000;
      lastRef.current = ts;
      tRef.current += dt;
      const t = tRef.current;
      const progress = 1 - countdown / COUNTDOWN_S;
      const color = urgent ? "#dc2626" : "#002DB5";
      ctx.clearRect(0, 0, 120, 120);
      orbits.forEach(o => {
        ctx.beginPath(); ctx.arc(cx, cy, o.r, 0, Math.PI * 2);
        ctx.strokeStyle = urgent ? "#dc262615" : "#002DB510";
        ctx.lineWidth = 1; ctx.stroke();
      });
      ctx.beginPath(); ctx.arc(cx, cy, 44, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
      ctx.strokeStyle = urgent ? "#dc262650" : "#002DB550";
      ctx.lineWidth = 3; ctx.lineCap = "round"; ctx.stroke();
      orbits.forEach(o => {
        const angle = t * o.speed + o.phase;
        const dx = cx + Math.cos(angle) * o.r, dy = cy + Math.sin(angle) * o.r;
        ctx.beginPath(); ctx.arc(dx, dy, o.dotR + 3, 0, Math.PI * 2);
        ctx.fillStyle = color; ctx.globalAlpha = 0.07; ctx.fill(); ctx.globalAlpha = 1;
        ctx.beginPath(); ctx.arc(dx, dy, o.dotR, 0, Math.PI * 2);
        ctx.fillStyle = color; ctx.globalAlpha = o.alpha; ctx.fill(); ctx.globalAlpha = 1;
      });
      animRef.current = requestAnimationFrame(draw);
    }
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [countdown, urgent]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(1,4,10,0.55)", backdropFilter: "blur(6px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ border: "1px solid rgba(0,45,181,0.15)" }}>
        <div className="px-7 py-5 flex items-center gap-4"
          style={{ background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
          <div className="w-10 h-10 rounded-full bg-white bg-opacity-10 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <p className="font-cinzel uppercase text-white opacity-60 tracking-widest" style={{ fontSize: "8px", letterSpacing: "0.28em" }}>Session Warning</p>
            <h2 className="font-cinzel font-bold text-white uppercase" style={{ fontSize: "14px", letterSpacing: "0.06em" }}>Are you still there?</h2>
          </div>
        </div>
        <div className="px-7 py-6 flex flex-col items-center gap-5">
          <p className="font-constantia text-gray-500 text-center leading-relaxed" style={{ fontSize: "14px" }}>
            You've been inactive for 5 minutes. For your security, you'll be automatically logged out in:
          </p>
          <div className="flex flex-col items-center gap-1">
            <canvas ref={canvasRef} width={120} height={120} />
            <span className="font-cinzel font-bold -mt-2"
              style={{ fontSize: "32px", color: urgent ? "#dc2626" : "#002DB5", transition: "color 0.4s" }}>
              {countdown}
            </span>
            <p className="font-cinzel uppercase text-[#002DB5] opacity-30 tracking-widest" style={{ fontSize: "8px", letterSpacing: "0.2em" }}>seconds remaining</p>
          </div>
          <div className="flex gap-3 w-full">
            <button onClick={onLogoutNow}
              className="flex-1 font-cinzel uppercase border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 py-3 rounded-xl transition-all duration-200"
              style={{ fontSize: "9px", letterSpacing: "0.15em" }}>Log Out</button>
            <button onClick={onStayLoggedIn}
              className="flex-1 font-cinzel uppercase text-white py-3 rounded-xl transition-all duration-200"
              style={{ fontSize: "9px", letterSpacing: "0.15em", background: "linear-gradient(135deg,#012E72,#002DB5)", boxShadow: "0 4px 20px rgba(0,45,181,0.3)" }}>Stay Logged In</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Toast Stack ───────────────────────────────────────────────────────
function ToastStack() {
  const { toasts, dismissToast } = useNotifications();
  if (!toasts.length) return null;

  return (
    <div style={{
      position: "fixed", bottom: "24px", right: "24px",
      zIndex: 9999, display: "flex", flexDirection: "column", gap: "10px",
      alignItems: "flex-end", pointerEvents: "none",
    }}>
      {toasts.map((toast) => (
        <div
          key={toast.tid}
          style={{
            pointerEvents: "all",
            display: "flex", alignItems: "flex-start", gap: "12px",
            backgroundColor: "#fff",
            border: `1px solid ${toast.cfg.accent}30`,
            borderLeft: `3px solid ${toast.cfg.accent}`,
            borderRadius: "14px",
            padding: "13px 16px",
            boxShadow: "0 8px 32px rgba(1,46,114,0.14), 0 2px 8px rgba(0,0,0,0.06)",
            minWidth: "290px", maxWidth: "340px",
            animation: "slideInToast 0.25s ease",
          }}
        >
          {/* Icon */}
          <div style={{
            width: "30px", height: "30px", borderRadius: "8px", flexShrink: 0,
            backgroundColor: toast.cfg.bg, color: toast.cfg.accent,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {toast.cfg.icon}
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              fontFamily: "'Cinzel', serif", fontSize: "9px",
              letterSpacing: "0.18em", textTransform: "uppercase",
              color: toast.cfg.accent, marginBottom: "3px",
            }}>
              {toast.cfg.label}
            </p>
            <p style={{
              fontFamily: "'Constantia', serif", fontSize: "13px",
              color: "#012E72", fontWeight: 600,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {toast.payload?.fullName || "New submission"}
            </p>
            {toast.payload?.email && (
              <p style={{
                fontFamily: "'Constantia', serif", fontSize: "12px",
                color: "rgba(1,46,114,0.45)", marginTop: "1px",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {toast.payload.email}
              </p>
            )}
          </div>

          {/* Dismiss */}
          <button
            onClick={() => dismissToast(toast.tid)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(1,46,114,0.25)", flexShrink: 0, padding: "2px",
              lineHeight: 1, transition: "color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#012E72"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(1,46,114,0.25)"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      ))}

      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ── Notification Bell + Dropdown ──────────────────────────────────────
function NotificationBell({ navigate }) {
  const { notifications, unreadCount, clearNotifications } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const ROUTE_MAP = {
    "consultation:new":     "/admin/consultations",
    "quote:new":            "/admin/quotes",
    "contact:new":          "/admin/contacts",
    "service-request:new":  "/admin/service-requests",
  };

  function timeAgo(date) {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          position: "relative", width: "40px", height: "40px",
          borderRadius: "10px", border: "1px solid rgba(1,46,114,0.12)",
          backgroundColor: open ? "rgba(1,46,114,0.06)" : "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "all 0.15s", color: "#012E72",
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.06)"}
        onMouseLeave={e => { if (!open) e.currentTarget.style.backgroundColor = "#fff"; }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: "-4px", right: "-4px",
            minWidth: "17px", height: "17px",
            backgroundColor: "#DC2626", color: "#fff",
            borderRadius: "999px", fontSize: "9px", fontWeight: 700,
            fontFamily: "Arial, sans-serif", lineHeight: "17px",
            textAlign: "center", padding: "0 4px",
            border: "2px solid #F7F4EF",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          width: "320px", backgroundColor: "#fff",
          borderRadius: "16px", border: "1px solid rgba(1,46,114,0.1)",
          boxShadow: "0 16px 48px rgba(1,46,114,0.14), 0 4px 12px rgba(0,0,0,0.06)",
          overflow: "hidden", zIndex: 100,
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 18px", display: "flex",
            alignItems: "center", justifyContent: "space-between",
            borderBottom: "1px solid rgba(1,46,114,0.07)",
            background: "linear-gradient(135deg, #012E72, #002DB5)",
          }}>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.22em", textTransform: "uppercase", color: "#fff", fontWeight: 700 }}>
              Notifications
            </p>
            {notifications.length > 0 && (
              <button
                onClick={clearNotifications}
                style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", background: "none", border: "none", cursor: "pointer", transition: "color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.color = "#fff"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
              >
                Clear all
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: "360px", overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "36px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(1,46,114,0.25)" }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    const route = ROUTE_MAP[n.type];
                    if (route) navigate(route);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%", display: "flex", alignItems: "flex-start", gap: "12px",
                    padding: "13px 18px", textAlign: "left", cursor: "pointer",
                    borderBottom: "1px solid rgba(1,46,114,0.05)",
                    background: "transparent", border: "none", borderBottom: "1px solid rgba(1,46,114,0.05)",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(1,46,114,0.025)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {/* Icon pill */}
                  <div style={{
                    width: "30px", height: "30px", borderRadius: "8px", flexShrink: 0,
                    backgroundColor: n.cfg.bg, color: n.cfg.accent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {n.cfg.icon}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em", textTransform: "uppercase", color: n.cfg.accent, marginBottom: "2px" }}>
                      {n.cfg.label}
                    </p>
                    <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {n.payload?.fullName || "New submission"}
                    </p>
                    {n.payload?.email && (
                      <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.payload.email}
                      </p>
                    )}
                  </div>

                  <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(1,46,114,0.28)", flexShrink: 0, marginTop: "2px" }}>
                    {timeAgo(n.at)}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inner layout (has access to NotificationContext) ──────────────────
function AdminLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen]               = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown]     = useState(COUNTDOWN_S);

  const currentAdmin = getTokenPayload();
  const isSuperAdmin = currentAdmin?.role === "super-admin";

  const inactiveTimer  = useRef(null);
  const countdownTimer = useRef(null);

  const [navBadges, setNavBadges] = useState({});

  const [searchQuery, setSearchQuery]     = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen]       = useState(false);
  const searchRef      = useRef(null);
  const searchTimerRef = useRef(null);

  // ── Init socket (one connection for entire admin session) ──
  useSocket();

  const handleSearchChange = useCallback((e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimerRef.current);
    if (!q.trim() || q.trim().length < 2) {
      setSearchResults(null);
      setSearchOpen(false);
      return;
    }
    setSearchOpen(true);
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await adminFetch(
          `${process.env.REACT_APP_API_URL}/api/admin/search?q=${encodeURIComponent(q.trim())}`
        );
        const data = await res.json();
        if (data.success) setSearchResults(data.results);
      } catch (_) {}
      setSearchLoading(false);
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults(null);
    setSearchOpen(false);
    clearTimeout(searchTimerRef.current);
  }, []);

  const logout = useCallback(async () => {
    try { await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/logout`, { method: "POST" }); } catch (_) {}
    localStorage.removeItem("paladin_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const resetInactivityTimer = useCallback(() => {
    if (showWarning) return;
    clearTimeout(inactiveTimer.current);
    inactiveTimer.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(COUNTDOWN_S);
    }, INACTIVE_MS);
  }, [showWarning]);

  useEffect(() => {
    if (!showWarning) { clearInterval(countdownTimer.current); return; }
    countdownTimer.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownTimer.current); logout(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownTimer.current);
  }, [showWarning, logout]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      clearTimeout(inactiveTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    function onClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.querySelector("input")?.focus();
        setSearchOpen(!!searchQuery.trim());
      }
      if (e.key === "Escape") setSearchOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [searchQuery]);

  useEffect(() => {
    async function fetchBadges() {
      try {
        const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/stats`);
        const data = await res.json();
        if (data.success) setNavBadges(data.data);
      } catch (_) {}
    }
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    setCountdown(COUNTDOWN_S);
    clearTimeout(inactiveTimer.current);
    inactiveTimer.current = setTimeout(() => { setShowWarning(true); setCountdown(COUNTDOWN_S); }, INACTIVE_MS);
  };
  const handleLogout = () => {
    clearTimeout(inactiveTimer.current);
    clearInterval(countdownTimer.current);
    logout();
  };

  const currentLabel = (() => {
    for (const sec of NAV_SECTIONS)
      for (const item of sec.items)
        if (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)) return item.label;
    return "Dashboard";
  })();

  const SIDEBAR_W = 260;
  const GAP = 14;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF" }}>

      {showWarning && (
        <InactivityWarning countdown={countdown} onStayLoggedIn={handleStayLoggedIn} onLogoutNow={handleLogout} />
      )}

      {/* ── Toast stack (global, bottom-right) ── */}
      <ToastStack />

      {/* ── Sidebar ── */}
      <aside
        className={[
          "fixed top-3 bottom-3 left-3 z-40 flex flex-col transition-transform duration-300",
          open ? "translate-x-0" : "-translate-x-[calc(100%+12px)] md:translate-x-0",
        ].join(" ")}
        style={{ width: `${SIDEBAR_W}px`, backgroundColor: "#F7F4EF", borderRadius: "16px" }}
      >
        {/* Logo */}
        <div className="px-5 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(1,46,114,0.10)" }}>
          <div className="flex items-center gap-3">
            <img src={PaladinLogo} alt="Paladin Logo" className="flex-shrink-0" style={{ width: "38px", height: "38px", objectFit: "contain" }} />
            <div>
              <p className="font-cinzel font-bold text-[#012E72] leading-tight" style={{ fontSize: "16px", letterSpacing: "0.08em" }}>PALADIN</p>
              <p className="font-cinzel uppercase leading-tight" style={{ fontSize: "9px", letterSpacing: "0.22em", color: "#94a3b8" }}>Insurance Admin</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-5 overflow-y-auto flex flex-col gap-5">
          {NAV_SECTIONS.map((section, si) => (
            <div key={si}>
              <p className="font-cinzel uppercase px-3 mb-2" style={{ fontSize: "9.5px", letterSpacing: "0.25em", color: "#94a3b8" }}>
                {section.section}
              </p>
              <div className="flex flex-col gap-0.5">
                {section.items.filter(item => !item.superOnly || isSuperAdmin).map(item => {
                  const isActive = item.end
                    ? location.pathname === item.to
                    : location.pathname.startsWith(item.to);
                  const badgeCount = navBadges[BADGE_KEY[item.to]] || 0;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setOpen(false)}
                      className="relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 overflow-hidden"
                      style={isActive ? { background: "#E2E8F0", color: "#012E72" } : { color: "#475569" }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#E8ECF0"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full"
                          style={{ height: "60%", background: "#002DB5" }} />
                      )}
                      <span className={isActive ? "opacity-100" : "opacity-70"}>{item.icon}</span>
                      <span className="font-cinzel uppercase flex-1" style={{ fontSize: "11px", letterSpacing: "0.12em", fontWeight: isActive ? 700 : 500 }}>
                        {item.label}
                      </span>
                      {badgeCount > 0 && (
                        <span style={{ background: isActive ? "#002DB5" : "#E8ECF0", color: isActive ? "#fff" : "#002DB5", borderRadius: "10px", fontSize: "9px", padding: "2px 6px", fontWeight: 700, fontFamily: "Arial, sans-serif", lineHeight: 1 }}>
                          {badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom: user + logout */}
        <div className="px-4 py-4" style={{ borderTop: "1px solid rgba(1,46,114,0.06)" }}>
          <div className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1" style={{ background: "#FFFFFF" }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#012E72,#002DB5)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-cinzel font-bold text-[#012E72] truncate leading-tight" style={{ fontSize: "11px", letterSpacing: "0.1em" }}>
                {currentAdmin?.name || "Administrator"}
              </p>
              <p className="font-cinzel truncate leading-tight" style={{ fontSize: "9px", letterSpacing: "0.1em", color: "#94a3b8" }}>
                {currentAdmin?.role === "super-admin" ? "Super Admin" : "Admin"} · @{currentAdmin?.username}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150"
            style={{ color: "#94a3b8" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#dc2626"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94a3b8"; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="font-cinzel uppercase" style={{ fontSize: "11px", letterSpacing: "0.13em" }}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-30 md:hidden"
          style={{ backgroundColor: "rgba(1,4,10,0.35)", backdropFilter: "blur(2px)" }}
          onClick={() => setOpen(false)} />
      )}

      {/* ── Right column ── */}
      <div className="flex flex-col h-screen overflow-hidden" style={{ paddingLeft: `${SIDEBAR_W + GAP + 12}px`, paddingRight: "12px" }}>

        {/* ── Topbar ── */}
        <div className="sticky top-3 z-20 mb-6 flex-shrink-0 relative">
          <header
            className="flex items-center gap-4 px-5"
            style={{ backgroundColor: "#F7F4EF", borderRadius: "14px", height: "64px" }}
          >
            {/* Mobile hamburger */}
            <button className="md:hidden text-[#012E72] flex-shrink-0" onClick={() => setOpen(v => !v)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>

            {/* Breadcrumb */}
            <div className="hidden md:flex items-center gap-3">
              <span className="font-constantia uppercase" style={{ fontSize: "15px", color: "#b0b8cc" }}>Admin</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
              <span className="font-cinzel uppercase font-bold text-[#012E72]" style={{ fontSize: "15px", letterSpacing: "0.12em" }}>{currentLabel}</span>
            </div>

            {/* Global search */}
            <div ref={searchRef} className="flex-1 max-w-sm relative mx-4">
              <div className="relative flex items-center">
                <svg className="absolute left-3 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => { if (searchQuery.trim().length >= 2) setSearchOpen(true); }}
                  placeholder="Search clients, quotes…"
                  className="w-full pl-8 pr-16 py-2 font-cinzel text-[#012E72] placeholder-[#94a3b8] outline-none transition-all duration-150"
                  style={{ background: "#FFFFFF", border: "1px solid #e2e8f0", borderRadius: "10px", fontSize: "11px", letterSpacing: "0.05em" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#c7d4e8"; }}
                  onMouseLeave={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = "#e2e8f0"; }}
                  onFocusCapture={e => { e.currentTarget.style.borderColor = "#002DB5"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0,45,181,0.1)"; }}
                  onBlurCapture={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
                />
                {searchQuery
                  ? <button onClick={clearSearch} className="absolute right-2 p-1 rounded-md transition-colors duration-150 hover:bg-gray-100">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  : <span className="absolute right-3 font-cinzel pointer-events-none" style={{ fontSize: "9px", letterSpacing: "0.1em", color: "#cbd5e1" }}>⌘K</span>
                }
              </div>
            </div>

            {/* ── Notification Bell ── */}
            <NotificationBell navigate={navigate} />

            {/* User info */}
            <div className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg,#012E72,#002DB5)", boxShadow: "0 2px 10px rgba(0,45,181,0.28)" }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="hidden md:block">
                <p className="font-cinzel font-bold text-[#012E72] leading-tight" style={{ fontSize: "13px", letterSpacing: "0.08em" }}>
                  {currentAdmin?.name || "Administrator"}
                </p>
                <p style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: "12px", color: "#94a3b8", lineHeight: 1.3 }}>
                  {currentAdmin?.role === "super-admin" ? "Super Admin" : "Admin"}
                </p>
              </div>
            </div>
          </header>

          {/* Search results dropdown */}
          {searchOpen && (
            <div className="absolute left-0 right-0 z-50 px-0" style={{ top: "calc(64px + 8px)" }}>
              <SearchDropdown
                query={searchQuery}
                results={searchResults}
                loading={searchLoading}
                onNavigate={(path) => { navigate(path); clearSearch(); }}
              />
            </div>
          )}
        </div>


        {/* Page content — only this scrolls */}
        <main className="flex-1 overflow-y-auto pb-5">
          <div style={{ backgroundColor: "#F7F4EF", borderRadius: "16px", minHeight: "100%", padding: "24px" }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ── Default export wraps inner layout with NotificationProvider ───────
export default function AdminLayout() {
  return (
    <NotificationProvider>
      <AdminLayoutInner />
    </NotificationProvider>
  );
}